import { History, Clock } from 'lucide-react'
import { useState, useEffect } from 'react'

interface HistoryItem {
    query: string
    timestamp: number
    status: string
}

interface QueryHistoryListProps {
    onSelectQuery: (query: string) => void
    lastQuery?: string
    lastStatus?: string
}

export function QueryHistoryList({ onSelectQuery, lastQuery, lastStatus }: QueryHistoryListProps) {
    const [history, setHistory] = useState<HistoryItem[]>([])

    // Load history
    useEffect(() => {
        const saved = localStorage.getItem('rag_query_history')
        if (saved) {
            try {
                setHistory(JSON.parse(saved))
            } catch (e) {
                console.error("Failed to parse history", e)
            }
        }
    }, [])

    // Update history when a new query is executed (passed via props change logic for simplicity in MVP)
    useEffect(() => {
        if (lastQuery && lastStatus) {
            setHistory(prev => {
                const newItem = { query: lastQuery, timestamp: Date.now(), status: lastStatus }
                // Avoid duplicates at the top
                if (prev.length > 0 && prev[0].query === lastQuery) return prev

                const newHistory = [newItem, ...prev].slice(0, 50)
                localStorage.setItem('rag_query_history', JSON.stringify(newHistory))
                return newHistory
            })
        }
    }, [lastQuery, lastStatus])

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col h-full max-h-[calc(100vh-200px)]">
            <div className="flex items-center gap-2 text-gray-300 mb-4 px-2">
                <History className="w-4 h-4" />
                <h3 className="text-sm font-semibold">History</h3>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                {history.map((item, i) => (
                    <div
                        key={i}
                        onClick={() => onSelectQuery(item.query)}
                        className="p-3 bg-black/40 hover:bg-white/10 border border-white/5 rounded-lg cursor-pointer group transition-colors"
                    >
                        <p className="text-xs text-gray-300 line-clamp-2 mb-2 group-hover:text-white">
                            {item.query}
                        </p>
                        <div className="flex justify-between items-center text-[10px] text-gray-500">
                            <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(item.timestamp).toLocaleTimeString()}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded ${item.status === 'ACCEPTED' ? 'bg-green-500/10 text-green-400' :
                                item.status === 'REFUSED' ? 'bg-red-500/10 text-red-400' : 'bg-gray-500/10 text-gray-400'
                                }`}>
                                {item.status}
                            </span>
                        </div>
                    </div>
                ))}

                {history.length === 0 && (
                    <div className="text-center text-xs text-gray-600 mt-10 italic">
                        No history yet.
                    </div>
                )}
            </div>
        </div>
    )
}
